import { useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import _, { take } from "lodash"

import { AppNavigation } from "containers/AppNavigation"

import { PageCard, PageCardContent } from "components/card"
import { VenueSelector } from "components/VenueSelector"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { MotionDiv } from "components/motions"
import { EmptyData } from "components/EmptyData"
import { DiningIcon } from "components/icons"
import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useDining } from "hooks/dining/useDining"
import { getFeatureDetail } from "utils/feature"
import { useSection } from "hooks/section"
import { OccupantImageCard } from "extensions/em-core/components/ImageCard"
import { useNavigate } from "react-router-dom"

const SECTION_DISPLAY_LIMIT = 4

export const DiningKioskHPage = ({ Layout = Box }) => {
  const { occupants, isMultiOrdinalVenue } = useVenue()
  const {
    restaurants,
    currentCategory,
    categories,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    showSubCategoryFilter,
    changeSubCategory,
    changeVenueFilter,
  } = useDining(occupants)
  const { filteredSectionsSorted } = useSection({
    category: "eatingdrinking",
  })
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )

  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}
  const { styleOverrides: bannerStyleOverrides = {} } =
    theme.components[`AppBanner`] || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}

  const {
    pageCard,
    contentWrapper,
    gridContainer,
    venueSelector,
    subCategoryFilterBar,
    scrollPane,
    emptyDataWrapper,
  } = styleOverrides

  const { root } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides

  const handleChangeSubCategory = useCallback(
    (_, id) => {
      changeSubCategory(id)
    },
    [changeSubCategory]
  )

  return (
    <Layout
      aside={
        <PageCard>
          <Box sx={wrapper}>
            <Box sx={root}></Box>
            <Box sx={body}>
              <AppNavigation />
            </Box>
            <Box sx={footer}>
              <NavigationControl BackButtonProps={{ hidden: true }} />
            </Box>
          </Box>
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={contentWrapper}
          >
            {restaurants.length > 0 || filteredSectionsSorted?.length > 0 ? (
              <Box sx={gridContainer}>
                {take(filteredSectionsSorted, SECTION_DISPLAY_LIMIT)
                  .concat(restaurants)
                  .map((restaurant) => {
                    const {
                      id,
                      level,
                      logo,
                      featuredImage,
                      name,
                      categoryIconPath,
                      isFeatured,
                    } = getFeatureDetail(restaurant) || {}

                    const restaurantName = t(name)
                    const isSection = restaurant?.feature_type === "section"

                    const levelText = isMultiOrdinalVenue
                      ? t("Level Text", { level: t(level) })
                      : ""

                    const subcategory = _.first(
                      restaurant?.properties?.local_sub_categories
                    )
                    const subcategoryName = _.get(
                      subcategory,
                      "properties.name"
                    )

                    return (
                      <OccupantImageCard
                        key={id}
                        id={id}
                        coverImage={featuredImage}
                        logoSrc={logo}
                        iconPath={categoryIconPath}
                        primaryText={restaurantName}
                        secondaryText={t(subcategoryName)}
                        adornmentText={levelText}
                        isFeatured={isFeatured}
                        isSection={isSection}
                        onClick={() => navigate(`/maps/place/${id}`)}
                      />
                    )
                  })}
              </Box>
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Stay tuned for the update")}
                />
              </Box>
            )}

            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={changeVenueFilter}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            {showSubCategoryFilter && (
              <Box sx={subCategoryFilterBar}>
                <HorizontalScrollPane
                  stackProps={{
                    sx: scrollPane,
                  }}
                >
                  <CategoryFilterBar
                    activeCategory={currentCategory}
                    categories={categories}
                    renderFilterButton={CategoryFilterChip}
                    onChange={handleChangeSubCategory}
                  />
                </HorizontalScrollPane>
              </Box>
            )}
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
